import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NO_ROLE_ASSIGNED } from "@/utils/constants/messages";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { getGroupById, getGroupUsers } from "@/services/group.service";
import { getUserById, updateUser } from "@/services/user.service";
import { BUTTONS, HEADINGS } from "@/utils/constants/appConstants/commonConstants";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import AppButton from '@/components/buttons/AppButton';
import { TabbedDetailListSkeleton, GROUP_DETAIL_TAB_WIDTHS } from "@/components/skeletons";
import GroupUsersTab from '@/components/tabs/GroupUsersTab';

const GroupDetails = () => {
    const isUpdateGroupPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalUpdateGroup);
    const isViewUserPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalViewUser);
    const [groupDetails, setGroupDetails] = useState({ name: "", roles: [] } as any);
    const [groupUsers, setGroupUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [groupUserLoading, setGroupUserLoading] = useState(true);
    const [selectedUserDetails, setSelectedUserDetails] = useState<any>();
    const [activeTab, setActiveTab] = useState<"roles" | "users">("roles");
    const currentOrganization = getCurrentOrganization();
    const params = useParams();
    const navigate = useNavigate();
    const id = params.id ? params.id : "";

    const fetchGroupDetails = () => {
        setLoading(true);
        getGroupById(
            id,
            (response: any) => {
                setGroupDetails(response.data);
                setLoading(false);
            },
            (error: any) => {
                console.log(error);
                setLoading(false);
            }
        );
    };

    const fetchGroupUsers = () => {
        setGroupUserLoading(true);
        getGroupUsers(
            id,
            (response: any) => {
                setGroupUsers(response.data.users);
                setGroupUserLoading(false);
            },
            (error: any) => {
                console.log(error);
                setGroupUserLoading(false);
            }
        );
    };

    useEffect(() => {
        fetchGroupDetails();
        fetchGroupUsers();
    }, [id]);

    const getUserDetails = (id: string) => {
        return getUserById(
            id,
            (response: any) => {
                setSelectedUserDetails(response.data);
            },
            (error: any) => {
                console.log(error);
            }
        );
    };

    useEffect(() => {
        if (!selectedUserDetails) return;
        const userOrganization = selectedUserDetails.organizations.filter((org: any) => org.organization === currentOrganization.organization);
        if (userOrganization?.length > 0) {
            const user_groups = userOrganization[0]?.groups;
            const new_group_ids = user_groups.filter((group: any) => group.id !== groupDetails.id).map((group: any) => group.id);

            const data = JSON.stringify({
                groups: new_group_ids,
            });

            updateUser(
                selectedUserDetails.id,
                data,
                () => {
                    const new_group_users = groupUsers.filter((user: any) => user.id !== selectedUserDetails.id);
                    setGroupUsers(new_group_users);
                },
                (error: any) => {
                    console.log(error);
                }
            );
        }
    }, [selectedUserDetails]);

    if (loading) {
        return <TabbedDetailListSkeleton tabWidths={GROUP_DETAIL_TAB_WIDTHS} />;
    }

    return (
        <>
            <div className="gs-detail-header">
                <h3 className="gs-detail-title capitalize" id="group-detail-name-title">{groupDetails.name}</h3>
                <div>
                    {isUpdateGroupPermitted &&
                        !!groupDetails &&
                        !currentOrganization?.groups.some((obj: any) => obj.id === groupDetails.id) && (
                            <AppButton
                                id="group-detail-edit-btn"
                                onClick={() => navigate(`/group/${groupDetails?.id}/edit`)}
                                btnText={BUTTONS.EDIT}
                                isLoading={false}
                                className="btn-primary"
                            />
                        )}
                </div>
            </div>
            <ul className="gs-page-tabs">
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "roles" ? " gs-page-tab--active" : ""}`}
                        id="group-detail-role-tab"
                        onClick={() => setActiveTab("roles")}
                    >
                        {HEADINGS.ROLES}
                    </button>
                </li>
                {isViewUserPermitted && (
                    <li>
                        <button
                            className={`gs-page-tab${activeTab === "users" ? " gs-page-tab--active" : ""}`}
                            id="group-detail-user-tab"
                            onClick={() => setActiveTab("users")}
                        >
                            {HEADINGS.USERS}
                        </button>
                    </li>
                )}
            </ul>
            <div>
                {activeTab === "roles" && (
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list">
                            {groupDetails.roles.length > 0 ? (
                                groupDetails.roles.map((role: any, key: number) => (
                                    <li className="gs-detail-list-item" key={role.id ?? key}>
                                        <div className="gs-detail-list-name" id={`group-detail-role-tab-name-${key}`}>{role.name}</div>
                                    </li>
                                ))
                            ) : (
                                <li className="gs-detail-list-item text-sm text-card-foreground" id="group-detail-rol-tab-no-data-message">
                                    {NO_ROLE_ASSIGNED}
                                </li>
                            )}
                        </ul>
                    </div>
                )}
                {isViewUserPermitted && activeTab === "users" && (
                    <GroupUsersTab groupUsers={groupUsers} getUserDetails={getUserDetails} isLoading={groupUserLoading} />
                )}
            </div>
        </>
    );
};

export default GroupDetails;
